import { useEffect, useState } from 'react'

import { useClipboard } from '@brands/ui/hooks'
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material'

import { Button } from '@/components/ui/button'
import { Copy04Icon, Download02Icon } from '@/components/ui/icons'
import { downloadFromObjectUrl } from '@/lib/file'
import { app } from '@/states/app-state'

interface Props {
  codes: string[]
  onContinue?: VoidFunction
}

const FILE_NAME = 'paydeck-recovery-codes.txt'

const INTRO_TEXT =
  'Save these recovery codes somewhere safe. Each one signs you in once if you lose your authenticator app.'

export function RecoveryCodesList({ codes, onContinue }: Props) {
  const clipboard = useClipboard({ timeout: 500 })

  const [saved, setSaved] = useState(false)

  const codesText = codes.join('\n')

  useEffect(() => {
    if (clipboard.copied) {
      app.showMessageToast({ variant: 'success', message: 'Recovery codes copied to clipboard' })
    } else if (clipboard.error) {
      app.showMessageToast({ variant: 'error', message: 'Failed to copy the recovery codes' })
    }
  }, [clipboard.copied, clipboard.error])

  const download = () => {
    const url = URL.createObjectURL(new Blob([codesText], { type: 'text/plain' }))
    downloadFromObjectUrl(url, FILE_NAME)
  }

  const print = () => {
    const printWindow = window.open('', '_blank')
    if (!printWindow) return

    const content = printWindow.document.createElement('pre')
    content.textContent = `PayDeck recovery codes\n\n${codesText}`
    printWindow.document.title = 'PayDeck recovery codes'
    printWindow.document.body.append(content)
    printWindow.print()
    printWindow.close()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="body2">{INTRO_TEXT}</Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 1,
          p: 2,
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
        }}
      >
        {codes.map((code) => (
          <Typography key={code} variant="body2" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
            {code}
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Copy04Icon sx={{ fontSize: 16 }} />}
          onClick={() => clipboard.copy(codesText)}
        >
          Copy
        </Button>
        <Button variant="outlined" size="small" startIcon={<Download02Icon sx={{ fontSize: 16 }} />} onClick={download}>
          Download
        </Button>
        <Button variant="outlined" size="small" onClick={print}>
          Print
        </Button>
      </Box>
      {onContinue && (
        <>
          <FormControlLabel
            label="I saved these codes"
            control={<Checkbox color="primary" checked={saved} onChange={(_, checked) => setSaved(checked)} />}
          />
          <Button color="primary" variant="contained" size="large" fullWidth disabled={!saved} onClick={onContinue}>
            Continue
          </Button>
        </>
      )}
    </Box>
  )
}
